import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, merge, Observable, Subject } from "rxjs";
import {
    filter,
    map,
    scan,
    share,
    shareReplay,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { MatbeskedDto } from "../dto/matbesked/matbesked-dto";
import { Viol3MatbeskedFetchedEventMessage } from "../kontrakt/kontrakt-utskrift/kontrakt-matbesked/Viol3MatbeskedFetchedEventMessage";
import { KontraktBilagorService } from "./kontrakt-bilagor.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

type MatbeskedReducer = (curr: MatbeskedDto) => MatbeskedDto;

@Injectable()
export class MatbeskedMediatorService {
    readonly matbesked$: Observable<MatbeskedDto>;
    readonly awaitingMatbesked$: Observable<boolean>;
    readonly sendingMatbesked$: Observable<boolean>;
    private readonly api = inject(ApiClientService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktBilagorService = inject(KontraktBilagorService);
    private readonly eventService = inject(EventService);
    private readonly createMatbeskedSrc = new Subject<void>();
    private readonly fetchMatbeskedSrc = new Subject<void>();
    private readonly awaitingMatbeskedSrc = new BehaviorSubject(false);
    private readonly sendingMatbeskedSrc = new BehaviorSubject(false);

    private readonly createMatbesked$ = this.createMatbeskedSrc.pipe(
        switchMap(() => this.kontraktMediatorService.kontrakt$),
        switchMap(kontrakt => this.postCreateMatbesked(kontrakt.id)),
        tap(() => this.sendingMatbeskedSrc.next(false)),
        share());

    private readonly getMatbesked$ = this.kontraktMediatorService.kontrakt$.pipe(
        filter(kontrakt => kontrakt.id > 0),
        switchMap(kontrakt => this.getMatbesked(kontrakt.id)),
        share()
    );

    private readonly fetchMatbesked$ = this.fetchMatbeskedSrc.pipe(
        switchMap(() => this.kontraktMediatorService.kontrakt$),
        switchMap(kontrakt => this.postFetchMatbesked(kontrakt.id)),
        share()
    );

    private readonly matbeskedEvent$ = this.eventService.subscribeEvent<Viol3MatbeskedFetchedEventMessage>(Viol3MatbeskedFetchedEventMessage.$type).pipe(
        withLatestFrom(this.kontraktMediatorService.kontrakt$),
        filter(([
            evt,
            kontrakt
        ]) => evt.kontraktId === kontrakt.id),
        map(([
            evt,
            _kontrakt
        ]) => {
            this.awaitingMatbeskedSrc.next(false);
            return evt;
        }),
        share()
    );

    constructor() {
        this.awaitingMatbesked$ = this.awaitingMatbeskedSrc.asObservable();
        this.sendingMatbesked$ = this.sendingMatbeskedSrc.asObservable();

        const updateMatbeskedReducer$: Observable<MatbeskedReducer> = merge(
            this.getMatbesked$,
            this.fetchMatbesked$,
            this.createMatbesked$).pipe(
            map(matbesked => (_curr: MatbeskedDto) => ({
                ...matbesked
            }))
        );

        const reloadBilagorReducer$: Observable<MatbeskedReducer> = this.matbeskedEvent$.pipe(
            tap(() =>  this.kontraktBilagorService.reloadBilagorSrc.next()),
            map(evt => (curr: MatbeskedDto) => ({
                ...curr,
                message: evt.message
            }))
        );

        const fetchMatbeskedReducer$: Observable<MatbeskedReducer> = this.fetchMatbesked$.pipe(
            map(() => (curr: MatbeskedDto) => curr)
        );

        this.matbesked$ = merge(
            reloadBilagorReducer$,
            updateMatbeskedReducer$,
            fetchMatbeskedReducer$
        ).pipe(
            scan((acc: MatbeskedDto, reducer: MatbeskedReducer) => reducer(acc), {}),
            shareReplay(1)
        );
    }

    createMatbesked() {
        this.sendingMatbeskedSrc.next(true);
        this.createMatbeskedSrc.next();
    }

    fetchMatbesked() {
        this.awaitingMatbeskedSrc.next(true);
        this.fetchMatbeskedSrc.next();
    }

    private getMatbesked(kontraktId: number): Observable<MatbeskedDto> {
        return this.api.get<MatbeskedDto>(`virkeskop2/matbesked/${kontraktId}`);
    }

    private postCreateMatbesked(kontraktId: number): Observable<MatbeskedDto> {
        return this.api.post<any, MatbeskedDto>(`virkeskop2/matbesked/${kontraktId}/create`, null);
    }

    private postFetchMatbesked(kontraktId: number) {
        return this.api.post<any, MatbeskedDto>(`virkeskop2/matbesked/${kontraktId}/fetch`, null);
    }
}
